"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { HistoryRange } from "@/lib/app-detail/daily-buckets";
import type { WorldwideRatingsResult } from "@/lib/app-detail/worldwide-ratings";

const EMPTY: WorldwideRatingsResult = { items: [], meta: { range: "all", points: 0, latest: null, latestAt: null, firstDate: null } };

/** The app's worldwide rating count over time, and how many new ratings a day it is picking up. */
export function useAppWorldwideRatings(appId: number, country: string, range: HistoryRange, enabled = true) {
  const { data, isPending, isFetching, error } = useQuery<WorldwideRatingsResult, Error>({
    queryKey: ["app-worldwide-ratings", appId, country, range],
    enabled: !!appId && enabled,
    placeholderData: keepPreviousData,
    // Sensor Tower fetches land a few times a day at most, and irregularly.
    staleTime: 12 * 60 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`/api/app-detail/worldwide-ratings?appId=${appId}&country=${country}&range=${range}`);
      if (!res.ok) throw new Error(`Failed to fetch worldwide ratings: ${res.statusText}`);
      return (await res.json()) as WorldwideRatingsResult;
    },
  });
  return { data: data ?? EMPTY, isLoading: isPending, isFetching, error: error?.message ?? null };
}
